import { useState, useEffect, useCallback } from 'react';
import {
  getCategories,
  addCategory,
  updateCategory,
  deleteCategory
} from '../firebase/salon'; // Funções estão no módulo salon.js
import { db } from '../firebase/firebase';
import { writeBatch, doc } from 'firebase/firestore';

/**
 * Hook para gerenciar operações de categorias
 * @param {string} salonId - ID do salão
 * @returns {object} - Métodos e estados para manipular categorias
 */
export const useCategories = (salonId) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Carrega todas as categorias do salão
   */
  const loadCategories = useCallback(async () => {
    if (!salonId) {
      setError('ID do salão não fornecido');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const categoriesData = await getCategories(salonId);
      // Filtrar categorias sem ID (proteção extra)
      const validCategories = categoriesData.filter(category => category && category.id);
      setCategories(validCategories);
      
      setLoading(false);
    } catch (err) {
      console.error('Erro ao carregar categorias:', err);
      setError(err.message || 'Erro ao carregar categorias');
      setLoading(false);
    }
  }, [salonId]);

  /**
   * Adiciona uma nova categoria
   * @param {object} categoryData - Dados da categoria
   * @returns {Promise<object|null>} - Categoria criada ou null se falhou
   */
  const handleAddCategory = async (categoryData) => {
    if (!salonId) {
      setError('ID do salão não fornecido');
      return null;
    }
    
    if (!categoryData || !categoryData.nome) {
      setError('Nome da categoria é obrigatório');
      return null;
    }
    
    try {
      setError(null);
      // Definir ordem como a última posição
      const newCategoryData = {
        ...categoryData,
        ordem: categories.length
      };
      
      const newCategory = await addCategory(salonId, newCategoryData);
      
      if (newCategory && newCategory.id) {
        setCategories(prev => [...prev, newCategory]);
        return newCategory;
      } else {
        throw new Error('Erro ao adicionar categoria: resposta inválida do servidor');
      }
    } catch (err) {
      console.error('Erro ao adicionar categoria:', err);
      setError(err.message || 'Erro ao adicionar categoria');
      return null;
    }
  };

  /**
   * Atualiza uma categoria existente
   * @param {string} categoryId - ID da categoria
   * @param {object} categoryData - Novos dados da categoria
   * @returns {Promise<object|null>} - Categoria atualizada ou null se falhou
   */
  const handleUpdateCategory = async (categoryId, categoryData) => {
    if (!salonId) {
      setError('ID do salão não fornecido');
      return null;
    }
    
    if (!categoryId) {
      setError('ID da categoria não fornecido');
      return null;
    }
    
    if (!categoryData || !categoryData.nome) {
      setError('Nome da categoria é obrigatório');
      return null;
    }
    
    try {
      setError(null);
      const updatedCategory = await updateCategory(salonId, categoryId, categoryData);
      
      if (updatedCategory) {
        setCategories(prev => 
          prev.map(category => category.id === categoryId ? { ...category, ...categoryData } : category)
        );
        return updatedCategory;
      } else {
        throw new Error('Erro ao atualizar categoria: resposta inválida do servidor');
      }
    } catch (err) {
      console.error('Erro ao atualizar categoria:', err);
      setError(err.message || 'Erro ao atualizar categoria');
      return null;
    }
  };

  /**
   * Remove uma categoria
   * @param {string} categoryId - ID da categoria
   * @returns {Promise<boolean>} - True se removida com sucesso
   */
  const handleDeleteCategory = async (categoryId) => {
    if (!salonId) {
      setError('ID do salão não fornecido');
      return false;
    }
    
    if (!categoryId) {
      setError('ID da categoria não fornecido');
      return false;
    }
    
    try {
      setError(null);
      await deleteCategory(salonId, categoryId);
      setCategories(prev => prev.filter(category => category.id !== categoryId));
      return true;
    } catch (err) {
      console.error('Erro ao excluir categoria:', err);
      setError(err.message || 'Erro ao excluir categoria');
      return false;
    }
  };

  /**
   * Atualiza a ordem das categorias
   * @param {Array} categoriesOrder - Array com IDs das categorias na ordem desejada
   * @returns {Promise<boolean>} - True se atualizado com sucesso
   */
  const handleUpdateCategoriesOrder = async (categoriesOrder) => {
    if (!salonId) {
      setError('ID do salão não fornecido');
      return false;
    }
    
    if (!categoriesOrder || !Array.isArray(categoriesOrder) || categoriesOrder.length === 0) {
      setError('Lista de categorias inválida');
      return false;
    }
    
    // Verificar se todos os IDs são válidos
    if (categoriesOrder.some(id => !id)) {
      setError('A lista contém IDs de categoria inválidos');
      return false;
    }
    
    try {
      setError(null);
      
      // Usar uma operação em lote para atualizar várias categorias
      const batch = writeBatch(db);
      
      categoriesOrder.forEach((categoryId, index) => {
        if (categoryId) {
          const categoryRef = doc(db, `salons/${salonId}/categories/${categoryId}`);
          batch.update(categoryRef, { ordem: index });
        }
      });
      
      await batch.commit();
      
      // Reorganizar a lista local de categorias
      const reorderedCategories = [];
      categoriesOrder.forEach((categoryId, index) => {
        const category = categories.find(c => c.id === categoryId);
        if (category) reorderedCategories.push({...category, ordem: index});
      });
      
      // Manter categorias que não estão na lista de ordem
      const otherCategories = categories.filter(c => !categoriesOrder.includes(c.id));
      
      setCategories([...reorderedCategories, ...otherCategories]);
      return true;
    } catch (err) {
      console.error('Erro ao atualizar ordem das categorias:', err);
      setError(err.message || 'Erro ao atualizar ordem das categorias');
      return false;
    }
  };

  // Carregar categorias na inicialização
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  return {
    categories,
    loading,
    error,
    loadCategories,
    addCategory: handleAddCategory,
    updateCategory: handleUpdateCategory,
    deleteCategory: handleDeleteCategory,
    updateCategoriesOrder: handleUpdateCategoriesOrder
  };
};

export default useCategories; 